import type { DeviceOption, UiErrorKind } from './diagnosticsTypes';

export const DEVICE_OPTIONS_CACHE_KEY = 'diagnostics.device_options.v1';
export const PAGE_TIMEOUT_MS = 10000;

export const isIpv4Literal = (s: string): boolean =>
  /^\d{1,3}(?:\.\d{1,3}){3}$/.test(String(s || '').trim());

export const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export function withTimeout<T>(promise: Promise<T>, label: string, timeoutMs: number = PAGE_TIMEOUT_MS): Promise<T> {
  let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutHandle = setTimeout(() => reject(new Error(`${label} timed out`)), timeoutMs);
  });
  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timeoutHandle) clearTimeout(timeoutHandle);
  }) as Promise<T>;
}

export function readDeviceOptionsCache(): DeviceOption[] {
  try {
    const raw = localStorage.getItem(DEVICE_OPTIONS_CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((d: any) => ({
        device_id: d?.device_id,
        name: d?.name,
        ip_address: d?.ip_address,
        status: d?.status,
        org_id: d?.org_id,
        site_id: d?.site_id,
      }))
      .filter((d: DeviceOption) => Boolean(d?.device_id));
  } catch {
    return [];
  }
}

export function writeDeviceOptionsCache(items: DeviceOption[]): void {
  try {
    localStorage.setItem(DEVICE_OPTIONS_CACHE_KEY, JSON.stringify(items || []));
  } catch {
    // ignore storage failures
  }
}

export function classifyAxiosError(e: any): { kind: UiErrorKind; message: string } {
  const status = e?.response?.status;
  const serverMsg = e?.response?.data?.error || e?.response?.data?.detail;
  const msg = String(serverMsg || e?.message || '').trim();

  if (!e?.response) {
    const code = String(e?.code || '').trim().toUpperCase();
    if (code === 'ECONNABORTED' || /timeout/i.test(msg)) {
      return {
        kind: 'network',
        message: 'Temporarily unavailable (device list request timed out from this browser session).',
      };
    }
    return {
      kind: 'network',
      message: 'Temporarily unavailable (cannot reach the cloud service from this browser session).',
    };
  }

  if (status === 401 || status === 403) {
    return {
      kind: 'auth',
      message: 'Unauthorized (please refresh/login again).',
    };
  }

  return { kind: 'other', message: msg || 'Request failed' };
}

export function networkQualityReasonPlain(reasonCode: string): string {
  const rc = String(reasonCode || '').trim();
  const m: Record<string, string> = {
    NETWORK_HEALTHY: 'No issue reported (healthy).',
    OFFLINE_MODE_ACTIVE: 'Offline mode is enabled - the edge is intentionally not relying on the cloud path.',
    UPLOAD_STAGE_DOWN: 'Incident or evidence uploader stage is down, so cloud uploads are blocked until it recovers.',
    OUTBOX_DEGRADED_BACKLOG: 'Upload backlog is large or very old - cloud API or uplink is struggling.',
    OUTBOX_BROWNOUT_BACKLOG: 'Some upload backlog - link may be flaky; this is short of a full outage.',
    network_quality_probe_failed: 'Network quality could not be evaluated on the edge.',
  };
  return m[rc] || rc;
}

export function describeNetworkQualityFromStage(st: any): string[] {
  const sig = st?.signals;
  const nq =
    sig && typeof sig === 'object' && sig.network_quality && typeof sig.network_quality === 'object'
      ? sig.network_quality
      : null;
  if (!nq) return [];
  const lines: string[] = [];
  const bucket = String(nq.bucket || '').trim().toLowerCase();
  if (bucket === 'offline') {
    lines.push('Cloud path class: offline (control plane treats MQTT/API as unavailable).');
  } else if (bucket === 'brownout') {
    lines.push('Cloud path class: brownout (reachable but impaired - often growing upload backlog).');
  } else if (bucket === 'degraded') {
    lines.push('Cloud path class: degraded (heavy stress on uploads or uplink).');
  } else if (bucket === 'healthy') {
    lines.push('Cloud path class: healthy.');
  } else if (bucket) {
    lines.push(`Cloud path class: ${bucket}`);
  }
  const rc = String(nq.reason_code || '').trim();
  if (rc) lines.push(networkQualityReasonPlain(rc));
  if (typeof nq.pending_total === 'number' && nq.pending_total > 0) {
    lines.push(`Pending in incident + evidence outboxes: ${nq.pending_total}`);
  }
  if (typeof nq.oldest_pending_age_sec === 'number' && nq.oldest_pending_age_sec > 0) {
    lines.push(`Oldest pending item: ~${Math.round(nq.oldest_pending_age_sec)}s old`);
  }
  if (typeof nq.incident_upload_status === 'string' && nq.incident_upload_status) {
    lines.push(`Incident uploader stage: ${nq.incident_upload_status}`);
  }
  if (typeof nq.evidence_upload_status === 'string' && nq.evidence_upload_status) {
    lines.push(`Evidence uploader stage: ${nq.evidence_upload_status}`);
  }
  if (nq.offline_overridden_by_keepalive_recency === true) {
    lines.push(
      'Offline flag in state file was ignored: a successful cloud keepalive was recent - this row uses backlog/upload signals instead (avoids false "down" for days).'
    );
  } else if (nq.offline_mode_active === true) {
    lines.push('Device reports offline mode active.');
  }
  return lines;
}

export function stageTriagePriority(stageId: string): number {
  const s = String(stageId || '').toLowerCase();
  if (s.includes('network_quality')) return 0;
  return 1;
}

export function formatGate1NetworkQualityHint(st: any): string | null {
  const nq = st?.signals?.network_quality;
  if (!nq || typeof nq !== 'object') return null;
  const b = String(nq.bucket || '').trim();
  const rc = String(nq.reason_code || '').trim();
  const parts: string[] = [];
  if (b) parts.push(`bucket=${b}`);
  if (rc) parts.push(rc);
  if (typeof nq.pending_total === 'number' && nq.pending_total > 0) {
    parts.push(`pending=${nq.pending_total}`);
  }
  if (nq.offline_overridden_by_keepalive_recency === true) {
    parts.push('reconciled:recent_keepalive');
  }
  return parts.length ? parts.join(' | ') : null;
}

export function networkQualityItPlaybook(reasonCode: string, bucket: string): string[] {
  const rc = String(reasonCode || '').trim();
  const b = String(bucket || '').trim().toLowerCase();

  if (rc === 'OFFLINE_MODE_ACTIVE' || b === 'offline') {
    return [
      'Confirm with your team whether this edge is supposed to run in offline mode at this site.',
      'If not: restore outbound access from the edge to the configured cloud API and MQTT broker (firewall rules, DNS, TLS inspection/proxy, correct hostnames and ports). Offline mode is usually set when keepalive to the cloud fails - it often clears automatically once the path works.',
      'After network changes, wait 1-2 minutes, then use Refresh on live pipeline health and Gate 1 stages.',
    ];
  }
  if (rc === 'UPLOAD_STAGE_DOWN') {
    return [
      'Click the incident_upload and evidence_upload chips (Pipeline & stage signals) and read last errors and outbox stats on this page.',
      'If your role allows it, use Restart uploaders (recommended actions on this stage, or other actions on Diagnostics).',
      'Check cloud credentials, device clock skew, and HTTP 401/403 in Edge Logs; fix auth before chasing "bandwidth".',
    ];
  }
  if (rc === 'OUTBOX_DEGRADED_BACKLOG') {
    return [
      'Large or very old upload backlog: treat as "cloud path cannot keep up or is failing" - confirm stable HTTPS from the edge to the cloud API (no intermittent proxy drops).',
      'Use Verify local (this page) to confirm edge services are up; then Restart uploaders after connectivity is stable.',
      'Fetch Edge Logs and search for upload, outbox, timeout, tls, reset - attach snippets if opening a vendor ticket.',
    ];
  }
  if (rc === 'OUTBOX_BROWNOUT_BACKLOG') {
    return [
      'Moderate backlog: link may be flaky or API slow. Check the same outbound path as above; compare with other sites on the same WAN.',
      'Restart uploaders once the path is stable; refresh stages to see pending_total drop.',
    ];
  }
  if (rc === 'network_quality_probe_failed') {
    return [
      'The edge did not return a full network_quality payload - often during restart. Wait a few minutes and refresh.',
      'Run Verify local on this page; if it fails, the cloud cannot reach the edge health API (routing/VPN/port).',
    ];
  }
  return [
    'Run Verify local on this page (same as the check_network_link recommended action) to pull edge service checks through the cloud.',
    'Fetch Edge Logs here and search for mqtt, broker, upload, outbox, tls, timeout, 401, or 403.',
    'From a shell on the edge network (or jump host), confirm DNS resolution and HTTPS to the cloud API and MQTT broker endpoints the deployment uses - not just from your laptop.',
    'If uploads were stuck and the path is fixed, use Restart uploaders, then refresh pipeline / Gate 1 data.',
  ];
}

export function recommendedActionButtonLabel(action: string): string {
  const a = String(action || '');
  const labels: Record<string, string> = {
    check_network_link: 'Run Verify local (edge)',
    restart_uploaders: 'Restart uploaders',
    sync_desired_state: 'Sync desired state',
    run_log_maintenance: 'Run log maintenance',
    reset_failed_evidence_upload: 'Reset failed evidence uploads',
    reset_failed_outbox: 'Reset failed outbox',
    replay_incident: 'Replay incident',
  };
  return labels[a] || a;
}
